import React, { useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import Checkbox from '@atlaskit/checkbox';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import DevicesIcon from '@atlaskit/icon/core/devices';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { JsmSelectHardwareTypesPayload } from '@atlassian/conversation-assistant-message-actions-api/jsm';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import { messages } from './messages';
import { SelectHardwareTypesCardSuccess } from './SelectHardwareTypesCardSuccess';

const styles = cssMap({
	outermostWrapper: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.150'),
		width: '100%',
		boxSizing: 'border-box',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	outerColumn: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		width: '100%',
		// No ADS token exists for container heights, so a raw pixel cap is used here.
		maxHeight: '360px',
		boxSizing: 'border-box',
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
	},
	titleSection: {
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		flexShrink: 0,
		gap: token('space.150'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	titleSection_collapsedEnd: {
		borderBottomLeftRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
	},
	headerIconWrap: {
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
	headingWrapper: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		minWidth: '0px',
	},
	expandToggleWrap: {
		marginInlineStart: 'auto',
	},
	contentSection: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		alignSelf: 'stretch',
		flex: '1 1 auto',
		// Allows the flex child to shrink below its content size so it can scroll.
		minHeight: '0px',
		boxSizing: 'border-box',
		overflowY: 'auto',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	hardwareTypeItem: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		width: '100%',
	},
	hardwareTypeIcon: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	hardwareTypeIconImg: {
		width: '24px',
		height: '24px',
	},
	hardwareTypeName: {
		flex: 1,
	},
	footerSection: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		alignSelf: 'stretch',
		flexShrink: 0,
		gap: token('space.100'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		backgroundColor: token('elevation.surface.raised'),
		borderBottomLeftRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

export type SelectHardwareTypesCardProps = {
	payload: JsmSelectHardwareTypesPayload;
	onNext: (selectedHardwareTypes: string[]) => void;
	onExpandToggle?: (isExpanded: boolean) => void;
	onHardwareTypeToggle?: (hardwareTypeName: string, isSelected: boolean) => void;
	isNextLoading?: boolean;
	isNextDisabled?: boolean;
	isSuccess?: boolean;
};

export const SelectHardwareTypesCard = ({
	payload,
	onNext,
	onExpandToggle,
	onHardwareTypeToggle,
	isNextLoading = false,
	isNextDisabled = false,
	isSuccess = false,
}: SelectHardwareTypesCardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(true);
	const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

	const ExpandCollapseIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;
	const showExpandedBody = !isSuccess && isExpanded;

	const heading = formatMessage(messages.cardTitle);

	const handleCheckboxChange = (index: number, checked: boolean) => {
		setSelectedIndices((prev) => {
			const nextSelectedIndices = new Set(prev);

			if (checked) {
				nextSelectedIndices.add(index);
			} else {
				nextSelectedIndices.delete(index);
			}

			return nextSelectedIndices;
		});

		onHardwareTypeToggle?.(payload.hardwareTypes[index]?.displayName ?? '', checked);
	};

	const hasSelection = selectedIndices.size > 0;

	const handleExpandToggle = () => {
		const nextIsExpanded = !isExpanded;
		setIsExpanded(nextIsExpanded);
		onExpandToggle?.(nextIsExpanded);
	};

	const handleNext = () => {
		const selectedDisplayNames = payload.hardwareTypes
			.filter((_, index) => selectedIndices.has(index))
			.map((hardwareType) => hardwareType.displayName);

		onNext(selectedDisplayNames);
	};

	return (
		<ErrorBoundary fallback={<ChatGenericError errorType="generic" />}>
			<Box xcss={styles.outermostWrapper} testId="select-hardware-types-card.root">
				{isSuccess ? (
					<SelectHardwareTypesCardSuccess
						heading={heading}
						successMessage={formatMessage(messages.successPill)}
					/>
				) : (
					<Box xcss={styles.outerColumn} testId="select-hardware-types-card">
						<Flex xcss={cx(styles.titleSection, !isExpanded && styles.titleSection_collapsedEnd)}>
							<Flex xcss={styles.headerIconWrap} alignItems="center" justifyContent="center">
								<DevicesIcon label="" spacing="none" color={token('color.icon.accent.blue')} />
							</Flex>
							<Box xcss={styles.headingWrapper}>
								<Heading size="xsmall">{heading}</Heading>
							</Box>
							<Box xcss={styles.expandToggleWrap}>
								<IconButton
									icon={ExpandCollapseIcon}
									label={formatMessage(
										isExpanded ? messages.collapseSection : messages.expandSection,
									)}
									appearance="subtle"
									spacing="compact"
									onClick={handleExpandToggle}
									testId="select-hardware-types-card.expand-toggle"
								/>
							</Box>
						</Flex>

						{showExpandedBody ? (
							<Box xcss={styles.contentSection} testId="select-hardware-types-card.options">
								<Stack space="space.100">
									{payload.hardwareTypes.map((hardwareType, index) => (
										<Box key={index} xcss={styles.hardwareTypeItem}>
											<Checkbox
												isChecked={selectedIndices.has(index)}
												onChange={(event) =>
													handleCheckboxChange(index, event.currentTarget.checked)
												}
												label={
													<Flex gap="space.100" alignItems="center">
														<Box xcss={styles.hardwareTypeIcon}>
															{hardwareType.iconUrl ? (
																<Box
																	as="img"
																	src={hardwareType.iconUrl}
																	alt=""
																	xcss={styles.hardwareTypeIconImg}
																/>
															) : (
																<DevicesIcon label="" color={token('color.icon')} />
															)}
														</Box>
														<Box xcss={styles.hardwareTypeName}>{hardwareType.displayName}</Box>
													</Flex>
												}
												testId={`select-hardware-types-card.checkbox-${index}`}
											/>
										</Box>
									))}
								</Stack>
							</Box>
						) : null}

						{showExpandedBody ? (
							<Flex xcss={styles.footerSection}>
								<Button
									appearance="primary"
									onClick={handleNext}
									isLoading={isNextLoading}
									isDisabled={isNextDisabled || !hasSelection}
									testId="select-hardware-types-card.next"
								>
									{formatMessage(messages.confirmButton)}
								</Button>
							</Flex>
						) : null}
					</Box>
				)}
			</Box>
		</ErrorBoundary>
	);
};
